import React, { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext, useReducer } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import '@fortawesome/fontawesome-free/css/all.min.css';

// ==============================================
// API SERVICE
// ==============================================
const BASE_URL = 'https://backend.railcab.in/api/v1/wadmin';

async function http(path, options = {}) {
  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json')
    ? await res.json().catch(() => null)
    : await res.text().catch(() => '');
  if (!res.ok) {
    const msg = typeof body === 'string' ? body : (body?.error?.message || body?.message || JSON.stringify(body));
    const err = new Error(`HTTP ${res.status}: ${msg}`);
    err.status = res.status; err.body = body;
    throw err;
  }
  // Unwrap the railcabv2 SuccessResponse envelope {Status, Message, Data}
  return body?.Data ?? body;
}

const api = {
  login: (email, password) => http('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  me: (token) => http('/auth/me', { headers: { Authorization: `Bearer ${token}` } }),
  refresh: (refreshToken) => http('/auth/refresh', { method: 'POST', body: JSON.stringify({ refreshToken }) }),
  listPages: (token) => http('/pages', { headers: { Authorization: `Bearer ${token}` } }),
  getPage: (token, id) => http(`/pages/${encodeURIComponent(id)}`, { headers: { Authorization: `Bearer ${token}` } }),
  createPage: (token, payload) => http('/pages', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) }),
  updatePage: (token, id, payload) => http(`/pages/${encodeURIComponent(id)}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) }),
  patchPage: (token, id, payload) => http(`/pages/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload)
  }),
  publishPage: (token, id) => http(`/pages/${encodeURIComponent(id)}/publish`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify({ status: 'PUBLISHED' }) }),
};

// ==============================================
// PAGE TEMPLATES
// ==============================================


// ==============================================
// UTILITY FUNCTIONS
// ==============================================
function extractLegacySection(layoutJson) {
  if (!Array.isArray(layoutJson)) return { index: -1, section: null };
  const idx = layoutJson.findIndex(s => s && s.type === 'LegacyCmsPage' && s.props);
  return { index: idx, section: idx >= 0 ? layoutJson[idx] : null };
}

function upsertLegacySection(layoutJson, legacyProps) {
  const safe = Array.isArray(layoutJson) ? [...layoutJson] : [];
  const { index } = extractLegacySection(safe);
  const section = { type: 'LegacyCmsPage', props: legacyProps };
  if (index >= 0) safe[index] = section; else safe.unshift(section);
  return safe;
}

function groupKeys(keysObj) {
  const keys = Object.keys(keysObj || {}).sort();
  const groups = {};
  for (const k of keys) {
    const parts = k.split('.');
    const group = parts.length >= 2 ? `${parts[0]}.${parts[1]}` : (parts[0] || 'misc');
    if (!groups[group]) groups[group] = [];
    groups[group].push(k);
  }
  return groups;
}

function isProbablyRichText(value) {
  if (typeof value !== 'string') return false;
  return /<\/?[a-z][\s\S]*>/i.test(value.trim());
}

function cloneDeep(v) {
  try {
    return JSON.parse(JSON.stringify(v));
  } catch {
    return v;
  }
}

// ==============================================
// STORE
// ==============================================
const StoreCtx = createContext(null);
const initialState = {
  auth: {
    accessToken: localStorage.getItem('rc_token') || '',
    refreshToken: localStorage.getItem('rc_refresh') || '',
    user: null,
  },
  pages: { list: [], byId: {} },
  ui: { loading: false, toasts: [] },
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING': return { ...state, ui: { ...state.ui, loading: action.payload } };
    case 'ADD_TOAST': return { ...state, ui: { ...state.ui, toasts: [...state.ui.toasts, action.payload] } };
    case 'REMOVE_TOAST': return { ...state, ui: { ...state.ui, toasts: state.ui.toasts.filter(t => t.id !== action.payload) } };
    case 'SET_TOKENS': {
      const { accessToken, refreshToken } = action.payload;
      if (accessToken !== undefined) localStorage.setItem('rc_token', accessToken || '');
      if (refreshToken !== undefined) localStorage.setItem('rc_refresh', refreshToken || '');
      return { ...state, auth: { ...state.auth, accessToken: accessToken ?? state.auth.accessToken, refreshToken: refreshToken ?? state.auth.refreshToken } };
    }
    case 'SET_USER': return { ...state, auth: { ...state.auth, user: action.payload } };
    case 'LOGOUT':
      localStorage.removeItem('rc_token'); localStorage.removeItem('rc_refresh');
      return { ...state, auth: { accessToken: '', refreshToken: '', user: null }, pages: { list: [], byId: {} } };
    case 'SET_PAGES': return { ...state, pages: { ...state.pages, list: Array.isArray(action.payload) ? action.payload : [] } };
    case 'CACHE_PAGE': return { ...state, pages: { ...state.pages, byId: { ...state.pages.byId, [action.payload.id]: action.payload.page } } };
    case 'UPDATE_CACHED_PAGE': {
      const existing = state.pages.byId[action.payload.id] || {};
      return { ...state, pages: { ...state.pages, byId: { ...state.pages.byId, [action.payload.id]: { ...existing, ...action.payload.updates } } } };
    }
    default: return state;
  }
}

function StoreProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const actions = useMemo(() => ({
    setLoading: v => dispatch({ type: 'SET_LOADING', payload: !!v }),
    toast: (msg, kind = 'info') => {
      const id = Date.now() + Math.random();
      dispatch({ type: 'ADD_TOAST', payload: { id, msg, kind } });
      setTimeout(() => dispatch({ type: 'REMOVE_TOAST', payload: id }), 4000);
    },
    setTokens: tokens => dispatch({ type: 'SET_TOKENS', payload: tokens }),
    setUser: user => dispatch({ type: 'SET_USER', payload: user }),
    logout: () => dispatch({ type: 'LOGOUT' }),
    setPages: list => dispatch({ type: 'SET_PAGES', payload: list }),
    cachePage: (id, page) => dispatch({ type: 'CACHE_PAGE', payload: { id, page } }),
    updateCachedPage: (id, updates) => dispatch({ type: 'UPDATE_CACHED_PAGE', payload: { id, updates } }),
  }), []);
  return <StoreCtx.Provider value={{ state, actions }}>{children}</StoreCtx.Provider>;
}
const useStore = () => { const c = useContext(StoreCtx); if (!c) throw new Error('useStore outside Provider'); return c; };

// ==============================================
// ROUTER
// ==============================================
const RouterCtx = createContext(null);
function RouterProvider({ children }) {
  const [hash, setHash] = useState(window.location.hash || '#/');
  useEffect(() => {
    const handle = () => setHash(window.location.hash || '#/');
    window.addEventListener('hashchange', handle);
    return () => window.removeEventListener('hashchange', handle);
  }, []);
  const navigate = useCallback(to => { window.location.hash = to; }, []);
  const route = hash.replace('#', '') || '/';
  return <RouterCtx.Provider value={{ route, navigate }}>{children}</RouterCtx.Provider>;
}
const useRouter = () => useContext(RouterCtx);

// ==============================================
// TOAST COMPONENT
// ==============================================
function ToastContainer() {
  const { state } = useStore();
  const icons = { success: 'fa-check-circle', error: 'fa-circle-xmark', info: 'fa-circle-info', warn: 'fa-triangle-exclamation' };
  const colors = { success: '#22c55e', error: '#ef4444', info: '#FF9500', warn: '#f59e0b' };

  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 99999, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 360 }}>
      {state.ui.toasts.map(t => (
        <div key={t.id} className="bg-white shadow-lg rounded-3 p-3 d-flex align-items-center gap-3" style={{ borderLeft: `3px solid ${colors[t.kind] || colors.info}` }}>
          <i className={`fa-solid ${icons[t.kind] || icons.info}`} style={{ color: colors[t.kind] || colors.info, fontSize: 15 }}></i>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{t.msg}</span>
        </div>
      ))}
    </div>
  );
}

// ==============================================
// LOGIN PAGE
// ==============================================
function LoginPage() {
  const { actions } = useStore();
  const { navigate } = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleLogin() {
    setBusy(true);
    try {
      const res = await api.login(email.trim(), password);
      actions.setTokens({ accessToken: res.accessToken, refreshToken: res.refreshToken });
      const me = await api.me(res.accessToken);
      actions.setUser(me);
      actions.toast('Welcome back!', 'success');
      navigate('#/pages');
    } catch (e) {
      actions.toast(e.message || 'Login failed', 'error');
    } finally { setBusy(false); }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0d0d16', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '20%', left: '30%', width: 400, height: 400, background: 'radial-gradient(circle,rgba(255,149,0,0.08),transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '20%', right: '25%', width: 300, height: 300, background: 'radial-gradient(circle,rgba(255,179,0,0.06),transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

      <div className="bg-white rounded-4 p-5" style={{ width: '100%', maxWidth: 420, position: 'relative' }}>
        <div className="d-flex align-items-center gap-3 mb-4">
          <div style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#FF9500,#FFB300)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🚆</div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#0d0d16' }}>RailCab<span style={{ color: '#FF9500' }}>.in</span></div>
            <div style={{ fontSize: 12, color: '#6c757d', marginTop: 1 }}>Admin CMS — Visual Editor</div>
          </div>
        </div>

        <div className="mb-4">
          <div style={{ fontSize: 24, fontWeight: 700, color: '#0d0d16' }}>Sign in</div>
          <div style={{ fontSize: 13, color: '#6c757d', marginTop: 4 }}>Manage your site content visually</div>
        </div>

        <label style={{ fontSize: 12, fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Email</label>
        <input className="form-control" style={{ marginBottom: 16 }} value={email} onChange={e => setEmail(e.target.value)} placeholder="User name " onKeyDown={e => e.key === 'Enter' && handleLogin()} />

        <label style={{ fontSize: 12, fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Password</label>
        <input className="form-control" style={{ marginBottom: 24 }} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" onKeyDown={e => e.key === 'Enter' && handleLogin()} />

        <button className="btn btn-primary w-100 py-3" onClick={handleLogin} disabled={busy} style={{ background: 'linear-gradient(135deg,#FF9500,#FFB300)', border: 'none' }}>
          {busy ? <><span className="spinner-border spinner-border-sm me-2"></span> Signing in…</> : <><i className="fa-solid fa-right-to-bracket me-2"></i> Sign in</>}
        </button>

        <div className="mt-4 p-3 bg-light rounded-3 small text-secondary">
          <i className="fa-solid fa-circle-info me-2" style={{ color: '#FF9500' }} />
          Connect your API at <code>https://backend.railcab.in/api/v1/wadmin</code> before signing in.
        </div>
      </div>
    </div>
  );
}

// ==============================================
// SIDEBAR
// ==============================================
function Sidebar({ collapsed, onToggle, currentView, onNavigate }) {
  const { state, actions } = useStore();
  const { navigate } = useRouter();

  function logout() {
    actions.logout();
    navigate('#/login');
  }

  const navItems = [
    { id: 'pages', icon: 'fa-file-lines', label: 'All Pages' },
    { id: 'editor', icon: 'fa-pen-ruler', label: 'Visual Editor' },
  ];

  return (
    <div style={{ width: collapsed ? 60 : 240, minWidth: collapsed ? 60 : 240, background: '#13131f', borderRight: '1px solid rgba(255,255,255,0.07)', display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0, zIndex: 50, transition: 'width 0.25s' }}>
      <div style={{ padding: '18px 14px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        {!collapsed && (
          <div className="d-flex align-items-center gap-2" style={{ overflow: 'hidden' }}>
            <div style={{ width: 34, height: 34, background: 'linear-gradient(135deg,#FF9500,#FFB300)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>🚆</div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'white', whiteSpace: 'nowrap' }}>RailCab<span style={{ color: '#FF9500' }}>.in</span></div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1, whiteSpace: 'nowrap' }}>Admin CMS</div>
            </div>
          </div>
        )}
        {collapsed && <div style={{ width: 34, height: 34, background: 'linear-gradient(135deg,#FF9500,#FFB300)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, margin: '0 auto' }}>🚆</div>}
        {!collapsed && <button className="btn btn-sm" onClick={onToggle} style={{ padding: 0, border: 'none', background: 'transparent', color: '#9ca3af' }}><i className="fa-solid fa-bars"></i></button>}
      </div>

      <div style={{ flex: 1, padding: '10px 8px', overflowY: 'auto' }}>
        {!collapsed && <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: '#6b7280', textTransform: 'uppercase', padding: '10px 4px 6px' }}>Content</div>}
        {navItems.map(item => (
          <button key={item.id} onClick={() => onNavigate(item.id)} title={collapsed ? item.label : ''}
            style={{ display: 'flex', alignItems: 'center', gap: 11, padding: collapsed ? '10px 0' : '9px 12px', borderRadius: 11, cursor: 'pointer', fontSize: 13.5, fontWeight: 500, border: 'none', width: '100%', textAlign: 'left', background: currentView === item.id ? 'rgba(255,149,0,0.12)' : 'transparent', color: currentView === item.id ? '#FF9500' : '#9ca3af', justifyContent: collapsed ? 'center' : 'flex-start' }}>
            <i className={`fa-solid ${item.icon}`} style={{ width: 17, textAlign: 'center', fontSize: 14 }}></i>
            {!collapsed && <span>{item.label}</span>}
          </button>
        ))}
      </div>

      <div style={{ padding: '10px 8px 14px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        {!collapsed && state.auth.user && (
          <div style={{ padding: '8px 12px', marginBottom: 6, borderRadius: 10, background: 'rgba(255,255,255,0.04)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{state.auth.user.email}</div>
            <div style={{ fontSize: 10, color: '#FF9500', marginTop: 2, fontWeight: 600 }}>{state.auth.user.role || 'ADMIN'}</div>
          </div>
        )}
        {collapsed && <button className="btn btn-sm w-100" onClick={logout} style={{ padding: '10px 0', border: 'none', background: 'transparent', color: '#ef4444' }}><i className="fa-solid fa-right-from-bracket"></i></button>}
        {!collapsed && (
          <>
            <button className="btn w-100 mb-1" onClick={logout} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px', borderRadius: 11, cursor: 'pointer', fontSize: 13.5, fontWeight: 500, border: 'none', background: 'transparent', color: '#ef4444' }}>
              <i className="fa-solid fa-right-from-bracket" style={{ width: 17, textAlign: 'center' }} />
              <span>Sign out</span>
            </button>
            <button className="btn w-100" onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px', borderRadius: 11, cursor: 'pointer', fontSize: 13.5, fontWeight: 500, border: 'none', background: 'transparent', color: '#6b7280' }}>
              <i className="fa-solid fa-chevron-left" style={{ width: 17, textAlign: 'center' }} />
              <span>Collapse</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ==============================================
// TOP BAR
// ==============================================
function TopBar({ currentPage, pages, onPageChange, editMode, onEditToggle, onAddPage, isSidebarCollapsed, onSidebarToggle }) {
  return (
    <div style={{ height: 56, minHeight: 56, background: '#13131f', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', flexShrink: 0 }}>
      {isSidebarCollapsed && (
        <button className="btn btn-sm" onClick={onSidebarToggle} style={{ padding: 0, border: 'none', background: 'transparent', color: '#9ca3af' }}><i className="fa-solid fa-bars"></i></button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0 }}>Page</span>
        <select className="form-select form-select-sm" style={{ maxWidth: 220, padding: '6px 10px', fontSize: 13, background: 'rgba(12, 12, 12, 0.8)', border: '1px solid rgba(255,255,255,0.07)', color: '#e8e8f4' }} value={currentPage?.id || ''} onChange={e => { const p = pages.find(x => x.id === e.target.value); if (p) onPageChange(p); }}>
          <option value="">— Select a page —</option>
          {pages.map(p => <option key={p.id} value={p.id}>{p.title} ({p.slug})</option>)}
        </select>
        {currentPage && (
          <span style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            /{currentPage.slug}.html
          </span>
        )}
      </div>

      <div className="d-flex align-items-center gap-2">
        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Edit</span>
        <div className={`form-check form-switch`}>
          <input className="form-check-input" type="checkbox" role="switch" id="editMode" checked={editMode} onChange={onEditToggle} style={{ cursor: 'pointer', backgroundColor: editMode ? '#FF9500' : undefined }} />
        </div>
      </div>

      <button className="btn btn-sm btn-outline-secondary" onClick={onAddPage} style={{ color: '#e8e8f4', borderColor: 'rgba(255,255,255,0.07)' }}><i className="fa-solid fa-plus me-1"></i> New Page</button>
    </div>
  );
}

// ==============================================
// INJECT STYLES AND SCRIPTS FOR IFRAME
// ==============================================




// ==============================================
// BUILD IFRAME DOCUMENT
// ==============================================
function buildIframeDoc(templateHtml, keys, collections) {
  let doc = templateHtml || '';
  if (doc.includes('</head>')) {
    doc = doc.replace('</head>', '<link rel="stylesheet" href="/assets/cms-inject.css" />\n</head>');
  }
  if (doc.includes('</body>')) {
    doc = doc.replace('</body>', '<script src="/assets/cms-inject.js"></script>\n</body>');
  }
  return doc;
}

// ==============================================
// PREVIEW FRAME
// ==============================================
// ==============================================
// PREVIEW FRAME - UPDATED
// ==============================================
function PreviewFrame({ page, editMode, onKeyChange, token }) {
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const lastPageId = useRef(null);
  const { actions } = useStore();

  useEffect(() => {
    if (!page) return;
    const frame = iframeRef.current;
    if (!frame) return;

    setLoading(true);
    lastPageId.current = page.id;



    const layout = page.layoutJson || [];
    const legacy = layout.find(s => s?.type === 'LegacyCmsPage');
    const keys = legacy?.props?.keys || {};
    const collections = legacy?.props?.collections || {};

    let tp = legacy?.props?.template || page.slug || 'home';
    fetch('/templates/' + tp + '.html')
      .then(async res => {
        let text = await res.text();
        if (!res.ok || text.includes('id="root"') || text.includes('<title>RailCab Admin</title>')) {
          const fallbackRes = await fetch('/templates/home.html');
          return fallbackRes.text();
        }
        return text;
      })
      .then(template => {
        const doc = buildIframeDoc(template, keys, collections);
        const handleLoad = () => {
          setLoading(false);
          frame.contentWindow?.postMessage({
            type: 'RC_SET_EDIT_MODE',
            value: editMode
          }, '*');

          frame.contentWindow?.postMessage({
            type: 'RC_APPLY_DATA',
            keys: keys,
            collections: collections
          }, '*');
        };

        frame.onload = handleLoad;
        frame.srcdoc = doc;
      })
      .catch(err => {
        console.error('Failed to load template:', err);
        setLoading(false);
      });
  }, [page?.id, page?.slug]);

  useEffect(() => {
    const frame = iframeRef.current;
    if (!frame?.contentWindow || !lastPageId.current) return;
    frame.contentWindow.postMessage({
      type: 'RC_SET_EDIT_MODE',
      value: editMode
    }, '*');
  }, [editMode]);

  useEffect(() => {
    const handle = async e => {
      if (e.data?.type === 'RC_CMS_CHANGE') {
        const { key, value, publish } = e.data;

        onKeyChange(key, value, publish);

        const frame = iframeRef.current;
        frame?.contentWindow?.postMessage({
          type: 'RC_APPLY_KEY',
          key,
          value
        }, '*');

        if (page && token) {
          try {
            actions.setLoading(true);

            await api.patchPage(token, page.id, {
              keys: {
                [key]: value
              }
            });

            if (publish) {
              actions.toast(`🚀 Published "${key.split('.').pop()}" to cloud`, 'success');
            } else {
              actions.toast(`💾 Saved "${key.split('.').pop()}" to cloud`, 'info');
            }

            const updatedPage = await api.getPage(token, page.id);
            actions.cachePage(page.id, updatedPage);

          } catch (err) {
            actions.toast(`Save failed: ${err.message}`, 'error');
            console.error('PATCH error:', err);
          } finally {
            actions.setLoading(false);
          }
        }
      }
    };

    window.addEventListener('message', handle);
    return () => window.removeEventListener('message', handle);
  }, [onKeyChange, page, token, actions]);

  if (!page) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-light">
        <div style={{ width: 64, height: 64, background: 'linear-gradient(135deg,rgba(255,149,0,0.15),rgba(255,179,0,0.08))', borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🚆</div>
        <div className="text-center mt-3">
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Select a page to preview</div>
          <div style={{ fontSize: 13, color: '#64748b' }}>Choose from the page dropdown above</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
      {loading && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(241,245,249,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, backdropFilter: 'blur(4px)' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status"></div>
            <div style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Loading preview…</div>
          </div>
        </div>
      )}
      <iframe
        id="preview-iframe"
        ref={iframeRef}
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        style={{ width: '100%', height: '100%', border: 'none', display: 'block', background: 'white' }}
        title="Preview"
      />
    </div>
  );
}

// ==============================================
// KEY EDITOR PANEL
// ==============================================
function KeyEditorPanel({ keysObj, onChange }) {
  const [search, setSearch] = useState('');
  const groups = useMemo(() => groupKeys(keysObj || {}), [keysObj]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    const out = {};
    for (const [g, list] of Object.entries(groups)) {
      const f = list.filter(k => k.toLowerCase().includes(q));
      if (f.length) out[g] = f;
    }
    return out;
  }, [groups, search]);

  const groupNames = Object.keys(filtered).sort();
  const totalKeys = Object.keys(keysObj || {}).length;

  function updateKey(key, val) { onChange({ ...(keysObj || {}), [key]: val }); }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 12, padding: 20, background: '#fff', color: '#000' }}>
      <div className="d-flex align-items-center gap-3 flex-wrap">
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#000', marginBottom: 2 }}>Content Keys</div>
          <div style={{ fontSize: 12, color: '#4b5563' }}>{totalKeys} keys across {Object.keys(groups).length} sections</div>
        </div>
        <input className="form-control" style={{ width: 200, padding: '7px 12px', fontSize: 12, background: '#fff', border: '1px solid #d1d5db', color: '#000' }} placeholder="Search keys…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {groupNames.length === 0 && (
          <div className="text-center p-5" style={{ color: '#4b5563' }}>
            {search ? 'No keys match your search.' : 'No keys yet. They appear here once the page has CMS data-cms-key attributes.'}
          </div>
        )}
        {groupNames.map(g => (
          <div key={g} style={{ background: '#f9fafb', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb' }}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <div style={{ width: 3, height: 20, background: '#000', borderRadius: '0 3px 3px 0' }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#000', textTransform: 'uppercase', letterSpacing: '0.08em', marginLeft: 8 }}>{g}</span>
              <span style={{ fontSize: 10, color: '#4b5563', marginLeft: 'auto' }}>{filtered[g].length} keys</span>
            </div>
            <div className="row g-2">
              {filtered[g].map(k => {
                const val = keysObj?.[k] ?? '';
                const rich = isProbablyRichText(val);
                const isLong = typeof val === 'string' && val.length > 100;
                const label = k.split('.').pop().replace(/[-_.]/g, ' ');
                return (
                  <div key={k} className="col-12 col-md-6">
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#000', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
                    {(rich || isLong) ? (
                      <textarea className="form-control" style={{ minHeight: 80, fontSize: 13, background: '#fff', border: '1px solid #d1d5db', color: '#000' }} value={val} onChange={e => updateKey(k, e.target.value)} spellCheck={false} />
                    ) : (
                      <input className="form-control" style={{ fontSize: 13, background: '#fff', border: '1px solid #d1d5db', color: '#000' }} value={val} onChange={e => updateKey(k, e.target.value)} />
                    )}
                    <div style={{ fontSize: 9, color: '#6b7280', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{k}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==============================================
// VISUAL EDITOR PAGE
// ==============================================
function VisualEditorPage({ selectedPageId, onPageChange, pages }) {
  const { state, actions } = useStore();
  const token = state.auth.accessToken;

  const [page, setPage] = useState(null);
  const [legacy, setLegacy] = useState({ page: '', keys: {}, collections: {} });
  const [editMode, setEditMode] = useState(true);
  const [activeTab, setActiveTab] = useState('preview');
  const [loading, setLoading] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(true);

  useEffect(() => {
    if (!selectedPageId) { setPage(null); return; }
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const cached = state.pages.byId[selectedPageId];
        const p = cached || await api.getPage(token, selectedPageId);
        if (!cancelled) {
          actions.cachePage(selectedPageId, p);
          setPage(p);
          const layout = p.layoutJson || [];
          const { section } = extractLegacySection(layout);
          const lp = section?.props || { page: p.slug, template: p.slug || 'home', keys: {}, collections: {} };
          setLegacy({ page: lp.page || p.slug, template: lp.template || p.slug || 'home', keys: cloneDeep(lp.keys || {}), collections: cloneDeep(lp.collections || {}) });
        }
      } catch (e) {
        if (!cancelled) actions.toast(e.message || 'Failed to load page', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPageId]);

  const handleKeyChange = useCallback((key, value, publish) => {
    setLegacy(l => ({ ...l, keys: { ...l.keys, [key]: value } }));
  }, []);

  async function save() {
    if (!page) return;

    actions.setLoading(true); setLoading(true);
    try {
      const layout = page.layoutJson || [];
      const updatedLayout = upsertLegacySection(Array.isArray(layout) ? layout : [], legacy);

      const payload = { layoutJson: updatedLayout };

      const saved = await api.updatePage(token, page.id, payload);
      actions.cachePage(page.id, saved);
      actions.setPages(state.pages.list.map(p => p.id === page.id ? { ...p, ...saved } : p));
      actions.toast('✅ Page saved successfully', 'success');
    } catch (e) { actions.toast(e.message || 'Save failed', 'error'); }
    finally { actions.setLoading(false); setLoading(false); }
  }

  async function publish() {
    if (!page) return;
    await save();
    actions.setLoading(true); setLoading(true);
    try {
      await api.publishPage(token, page.id);
      const updatedPage = await api.getPage(token, page.id);
      actions.cachePage(page.id, updatedPage);
      setPage(updatedPage);
      actions.setPages(state.pages.list.map(p => p.id === page.id ? updatedPage : p));
      actions.toast('🚀 Page published! Live on site.', 'success');
    } catch (e) { actions.toast(e.message || 'Publish failed', 'error'); }
    finally { actions.setLoading(false); setLoading(false); }
  }

  const previewPage = useMemo(() => {
    if (!page) return null;
    const layout = page.layoutJson || [];
    const updated = upsertLegacySection(Array.isArray(layout) ? layout : [], legacy);
    return { ...page, layoutJson: updated };
  }, [page, legacy]);

  const PANEL_TABS = [
    { id: 'preview', label: 'Preview', icon: 'fa-eye' },
    { id: 'keys', label: 'Keys', icon: 'fa-pen-to-square' },
  ];

  if (!selectedPageId) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100">
        <div style={{ fontSize: 48 }}>🎨</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'white' }}>Visual Editor</div>
        <div style={{ color: '#6b7280', fontSize: 14 }}>Select a page from "All Pages" or the dropdown above</div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#13131f', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0, overflowX: 'auto' }}>
          {PANEL_TABS.map(t => (
            <button key={t.id} className={`btn btn-sm ${activeTab === t.id ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setActiveTab(t.id)} style={{ border: 'none', background: activeTab === t.id ? 'rgba(255,149,0,0.15)' : 'transparent', color: activeTab === t.id ? '#FF9500' : '#9ca3af' }}>
              <i className={`fa-solid ${t.icon} me-1`} /> {t.label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          {activeTab === 'preview' && (
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Edit Mode</span>
              <div className="form-check form-switch">
                <input className="form-check-input" type="checkbox" role="switch" id="editModePreview" checked={editMode} onChange={() => setEditMode(v => !v)} style={{ cursor: 'pointer' }} />
              </div>
            </div>
          )}
          <button className="btn btn-sm" onClick={() => setShowRightPanel(v => !v)} style={{ background: showRightPanel ? 'rgba(255,149,0,0.15)' : 'rgba(255,255,255,0.06)', color: showRightPanel ? '#FF9500' : '#9ca3af', border: 'none' }}>
            <i className="fa-solid fa-panel-right"></i>
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          {activeTab === 'preview' && (
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
              <PreviewFrame
                page={previewPage}
                editMode={editMode}
                onKeyChange={handleKeyChange}
                token={token}
              />
            </div>
          )}
          {activeTab === 'keys' && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <KeyEditorPanel keysObj={legacy.keys} onChange={keys => setLegacy(l => ({ ...l, keys }))} />
            </div>
          )}
        </div>
      </div>

      {showRightPanel && (
        <div style={{ width: 280, minWidth: 280, background: '#13131f', borderLeft: '1px solid rgba(255,255,255,0.07)', display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'white', marginBottom: 2 }}>Page Info</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>{page?.slug || '–'}</div>
          </div>
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Status</div>
              <div className="d-flex align-items-center gap-2">
                <span className={`badge ${page?.status === 'PUBLISHED' ? 'bg-success' : 'bg-warning'}`}>
                  {page?.status || 'Draft'}
                </span>
              </div>
            </div>

            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Content</div>
              <div className="d-flex gap-2 flex-wrap">
                <div className="bg-secondary bg-opacity-10 rounded-3 p-2 text-center flex-fill">
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#FF9500' }}>{Object.keys(legacy.keys || {}).length}</div>
                  <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>Keys</div>
                </div>
              </div>
            </div>

            {page?.updatedAt && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Last Updated</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(page.updatedAt).toLocaleString()}</div>
              </div>
            )}

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button className="btn btn-outline-secondary btn-sm w-100" onClick={save} disabled={loading} style={{ color: '#e8e8f4' }}>
                {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="fa-solid fa-floppy-disk me-2"></i>} Save Draft
              </button>
              <button className="btn btn-primary btn-sm w-100" onClick={publish} disabled={loading} style={{ background: 'linear-gradient(135deg,#FF9500,#FFB300)', border: 'none' }}>
                <i className="fa-solid fa-rocket me-2"></i> Publish Live
              </button>
            </div>

            <div className="p-3 small text-secondary" style={{ background: 'rgba(255,149,0,0.05)', border: '1px solid rgba(255,149,0,0.12)', borderRadius: 10 }}>
              <strong style={{ color: '#FF9500' }}>💡 Tip:</strong> In Preview tab with Edit Mode on, hover any text on the page and click to edit it inline. Changes save instantly to cloud.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==============================================
// ADD PAGE MODAL
// ==============================================
function AddPageModal({ onClose, onCreated }) {
  const { state, actions } = useStore();
  const [form, setForm] = useState({ title: '', slug: '', navLabel: '', template: 'home', showInNav: true, metaDescription: '' });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  async function create() {
    if (!form.title.trim() || !form.slug.trim()) { actions.toast('Title and slug are required', 'error'); return; }
    setBusy(true);
    try {
      const layoutJson = [{ type: 'LegacyCmsPage', props: { page: form.slug.trim(), template: form.template, keys: {}, collections: {} } }];
      const payload = { title: form.title, slug: form.slug.trim(), navLabel: form.navLabel || form.title, showInNav: form.showInNav, navOrder: 99, metaDescription: form.metaDescription || null, layoutJson };
      const created = await api.createPage(state.auth.accessToken, payload);
      actions.setPages([...(state.pages.list || []), created]);
      actions.toast(`✨ Page "${form.title}" created!`, 'success');
      onCreated(created);
      onClose();
    } catch (e) { actions.toast(e.message || 'Failed to create page', 'error'); }
    finally { setBusy(false); }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
      <div className="bg-white rounded-4 p-4" style={{ width: '100%', maxWidth: 480 }}>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0d0d16' }}>✨ New Page</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Add a new page to the RailCab website</div>
          </div>
          <button className="btn-close" onClick={onClose}></button>
        </div>

        <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Page Title *</label>
        <input className="form-control mb-3" value={form.title} placeholder="e.g. About Us" onChange={e => { set('title', e.target.value); set('slug', autoSlug(e.target.value)); set('navLabel', e.target.value); }} />

        <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Slug (URL path) *</label>
        <input className="form-control mb-1" value={form.slug} placeholder="e.g. about-us" onChange={e => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} />
        <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 14 }}>URL will be: /{form.slug || 'page-slug'}</div>

        <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Base Template *</label>
        <select className="form-select mb-3" value={form.template} onChange={e => set('template', e.target.value)}>
          {['home', 'contact', 'company', 'gallery', 'privacy', 'terms', 'legal', 'services', 'faqs', 'service-details'].map(t => <option key={t} value={t}>{t}.html ({t})</option>)}
        </select>

        <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Nav Label</label>
        <input className="form-control mb-3" value={form.navLabel} placeholder="Navigation menu text" onChange={e => set('navLabel', e.target.value)} />

        <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Meta Description</label>
        <textarea className="form-control mb-3" rows={2} value={form.metaDescription} placeholder="SEO description..." onChange={e => set('metaDescription', e.target.value)} />

        <div className="form-check mb-4">
          <input type="checkbox" className="form-check-input" id="showInNav" checked={form.showInNav} onChange={e => set('showInNav', e.target.checked)} />
          <label className="form-check-label" htmlFor="showInNav">Show in navigation menu</label>
        </div>

        <div className="d-flex gap-2 justify-content-end">
          <button className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={create} disabled={busy} style={{ background: 'linear-gradient(135deg,#FF9500,#FFB300)', border: 'none' }}>
            {busy ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="fa-solid fa-plus me-2"></i>} Create Page
          </button>
        </div>
      </div>
    </div>
  );
}

// ==============================================
// PAGES LIST VIEW
// ==============================================
function PagesListView({ onEditPage, onAddPage }) {
  const { state, actions } = useStore();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const list = await api.listPages(state.auth.accessToken);
        if (!cancelled) actions.setPages(list);
      } catch (e) {
        if (!cancelled) actions.toast(e.message || 'Failed to load pages', 'error');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [state.auth.accessToken]);

  const pages = (state.pages.list || []).filter(p =>
    !search || p.title?.toLowerCase().includes(search.toLowerCase()) || p.slug?.toLowerCase().includes(search.toLowerCase())
  );

  async function handlePublish(p) {
    if (window.confirm(`Publish "${p.title}" immediately?`)) {
      setLoading(true);
      try {
        await api.publishPage(state.auth.accessToken, p.id);
        const updated = await api.getPage(state.auth.accessToken, p.id);
        actions.cachePage(p.id, updated);
        actions.setPages(state.pages.list.map(x => x.id === p.id ? updated : x));
        actions.toast('🚀 Page published!', 'success');
      } catch (e) {
        actions.toast(e.message || 'Publish failed', 'error');
      } finally {
        setLoading(false);
      }
    }
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'white' }}>All Pages</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>Manage your site pages and content</div>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <input className="form-control" style={{ width: 220, padding: '8px 14px', fontSize: 13, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)', color: '#e8e8f4' }} placeholder="Search pages…" value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-primary" onClick={onAddPage} style={{ background: 'linear-gradient(135deg,#FF9500,#FFB300)', border: 'none' }}><i className="fa-solid fa-plus me-1"></i> New Page</button>
        </div>
      </div>

      <div className="bg-secondary bg-opacity-10 rounded-3 overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="row p-3 m-0" style={{ background: 'rgba(0, 0, 0, 0.32)', borderBottom: '1px solid rgba(14, 13, 13, 0.07)' }}>
          <div className="col-3" style={{ fontSize: 10, fontWeight: 700, color: '#0000009d', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Title</div>
          <div className="col-2" style={{ fontSize: 10, fontWeight: 700, color: '#0000009d', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Slug</div>
          <div className="col-2" style={{ fontSize: 10, fontWeight: 700, color: '#0000009d', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Status</div>
          <div className="col-1" style={{ fontSize: 10, fontWeight: 700, color: '#0000009d', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Nav</div>
          <div className="col-2" style={{ fontSize: 10, fontWeight: 700, color: '#0000009d', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Updated</div>
          <div className="col-2" style={{ fontSize: 10, fontWeight: 700, color: '#0000009d', textTransform: 'uppercase', letterSpacing: '0.1em' }}></div>
        </div>

        {loading && (
          <div className="text-center p-5">
            <div className="spinner-border text-primary mb-3" role="status"></div>
            <div style={{ fontSize: 13, color: 'black' }}>Loading pages…</div>
          </div>
        )}

        {!loading && pages.map((p, i) => (
          <div key={p.id} className="row p-3 m-0 align-items-center" style={{ borderBottom: i < pages.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none' }}>
            <div className="col-3">
              <div style={{ fontSize: 13, fontWeight: 600, color: 'black' }}>{p.title}</div>
              {p.metaDescription && <div style={{ fontSize: 11, color: '#00000000', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 250 }}>{p.metaDescription}</div>}
            </div>
            <div className="col-2" style={{ fontSize: 11, color: 'black', fontFamily: 'monospace' }}>/{p.slug}</div>
            <div className="col-2"><span className={`badge ${p.status === 'PUBLISHED' ? 'bg-success' : 'bg-warning'}`}>{p.status === 'PUBLISHED' ? 'Live' : 'Draft'}</span></div>
            <div className="col-1" style={{ fontSize: 12, color: p.showInNav ? '#22c55e' : '#6b7280' }}>{p.showInNav ? 'Yes' : 'No'}</div>
            <div className="col-2" style={{ fontSize: 11, color: '#6b7280' }}>{p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : '–'}</div>
            <div className="col-2 text-end" style={{ whiteSpace: 'nowrap' }}>
              {p.status !== 'PUBLISHED' && (
                <button className="btn btn-sm btn-outline-success me-2" onClick={() => handlePublish(p)} title="Publish"><i className="fa-solid fa-rocket"></i> Publish</button>
              )}
              <button className="btn btn-sm btn-primary" onClick={() => onEditPage(p)} style={{ background: 'linear-gradient(135deg,#FF9500,#FFB300)', border: 'none' }} title="Edit"><i className="fa-solid fa-pen-to-square"></i></button>
            </div>
          </div>
        ))}

        {!loading && pages.length === 0 && (
          <div className="text-center p-5">
            <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'white', marginBottom: 6 }}>No pages found</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>{search ? 'Try a different search term.' : 'Get started by creating your first page.'}</div>
            {!search && <button className="btn btn-primary" onClick={onAddPage} style={{ background: 'linear-gradient(135deg,#FF9500,#FFB300)', border: 'none' }}><i className="fa-solid fa-plus me-1"></i> Create First Page</button>}
          </div>
        )}
      </div>
    </div>
  );
}

// ==============================================
// STATUS BAR
// ==============================================
function StatusBar({ loading, pageTitle, editMode }) {
  return (
    <div style={{ height: 28, minHeight: 28, background: '#13131f', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 16, padding: '0 14px', fontSize: 11, color: '#6b7280', flexShrink: 0 }}>
      <span className="d-flex align-items-center gap-1">
        <span style={{ width: 7, height: 7, borderRadius: '50%', display: 'inline-block', background: loading ? '#f59e0b' : '#22c55e' }} />
        {loading ? 'Working…' : 'Ready'}
      </span>
      {pageTitle && <span style={{ color: '#9ca3af' }}>Editing: <span style={{ color: 'white', fontWeight: 600 }}>{pageTitle}</span></span>}
      {editMode !== undefined && <span>Mode: <span style={{ color: '#FF9500', fontWeight: 600 }}>{editMode ? 'Edit' : 'Preview'}</span></span>}
      <span style={{ marginLeft: 'auto', fontWeight: 700, color: 'rgba(255,149,0,0.4)', fontSize: 10, letterSpacing: '0.1em' }}>RAILCAB CMS v1.0</span>
    </div>
  );
}

// ==============================================
// MAIN APP
// ==============================================
function App() {
  const { state, actions } = useStore();
  const { route, navigate } = useRouter();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentView, setCurrentView] = useState('pages');
  const [selectedPage, setSelectedPage] = useState(null);
  const [showAddPage, setShowAddPage] = useState(false);
  const [editMode, setEditMode] = useState(true);

  const isLoggedIn = !!state.auth.accessToken;

  useEffect(() => {
    if (!state.auth.accessToken || state.auth.user) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await api.me(state.auth.accessToken);
        if (!cancelled) actions.setUser(me);
      } catch { if (!cancelled) actions.logout(); }
    })();
    return () => { cancelled = true; };
  }, [state.auth.accessToken]);

  if (!isLoggedIn) return <LoginPage />;

  function handleEditPage(page) {
    setSelectedPage(page);
    setCurrentView('editor');
  }

  function handleNavigate(view) {
    setCurrentView(view);
  }

  const pages = state.pages.list || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} currentView={currentView} onNavigate={handleNavigate} />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <TopBar
            currentPage={selectedPage}
            pages={pages}
            onPageChange={p => { setSelectedPage(p); setCurrentView('editor'); }}
            editMode={editMode}
            onEditToggle={() => setEditMode(v => !v)}
            onAddPage={() => setShowAddPage(true)}
            loading={state.ui.loading}
            isSidebarCollapsed={sidebarCollapsed}
            onSidebarToggle={() => setSidebarCollapsed(v => !v)}
          />

          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {currentView === 'pages' && (
              <PagesListView onEditPage={handleEditPage} onAddPage={() => setShowAddPage(true)} />
            )}
            {currentView === 'editor' && (
              <VisualEditorPage
                selectedPageId={selectedPage?.id}
                onPageChange={setSelectedPage}
                pages={pages}
              />
            )}
          </div>

          <StatusBar loading={state.ui.loading} pageTitle={selectedPage?.title} editMode={currentView === 'editor' ? editMode : undefined} />
        </div>
      </div>

      {showAddPage && (
        <AddPageModal
          onClose={() => setShowAddPage(false)}
          onCreated={page => { setSelectedPage(page); setCurrentView('editor'); }}
        />
      )}

      <ToastContainer />
    </div>
  );
}

// ==============================================
// EXPORT
// ==============================================
export default function RailCabCMS() {
  return (
    <StoreProvider>
      <RouterProvider>
        <App />
      </RouterProvider>
    </StoreProvider>
  );
}